import { useCallback, useEffect, useRef, useState } from 'react';

// `render=explicit` keeps Turnstile from grabbing elements on its own, so the
// widget is created only when this hook says so.
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

type TurnstileOptions = {
  sitekey: string;
  appearance: 'always' | 'execute' | 'interaction-only';
  size: 'normal' | 'flexible' | 'compact';
  language: string;
  callback: (token: string) => void;
  'error-callback': (code: string) => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | undefined;

const loadScript = () => {
  if (!scriptPromise)
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = undefined;
        reject(new Error('Turnstile script failed to load'));
      };
      document.head.appendChild(script);
    });

  return scriptPromise;
};

export type CaptchaStatus = 'idle' | 'solving' | 'ready' | 'error';

export type Captcha = {
  containerRef: (node: HTMLDivElement | null) => void;
  token: string | undefined;
  status: CaptchaStatus;
  reset: () => void;
};

/**
 * Holds a Cloudflare Turnstile token for the payment about to be submitted.
 *
 * The widget is rendered into `containerRef`, which the caller places in its own
 * layout — an interactive challenge has to appear next to the button it blocks,
 * not floating over the page. `interaction-only` keeps it invisible for the
 * visitors Cloudflare can clear silently, which is nearly all of them.
 *
 * `enabled` exists so the challenge can start a step before the token is needed:
 * by the time the visitor reaches the confirmation step the token is usually
 * already in hand, and the button never waits on it.
 */
export const useCaptcha = ({ enabled }: { enabled: boolean }): Captcha => {
  // A callback ref rather than a plain one: the payment view tears its button
  // area down while a payment is in flight, and tracking the node as state is
  // what lets the widget be rebuilt when that area comes back.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);
  const widgetIdRef = useRef<string>();
  const [token, setToken] = useState<string>();
  const [status, setStatus] = useState<CaptchaStatus>('idle');

  useEffect(() => {
    if (!enabled || !container) return;

    if (!SITE_KEY) {
      console.error('rentalsPaymentsPortal useCaptcha missing VITE_TURNSTILE_SITE_KEY');
      setStatus('error');
      return;
    }

    let cancelled = false;
    setToken(undefined);
    setStatus('solving');

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;

        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: SITE_KEY,
          appearance: 'interaction-only',
          size: 'flexible',
          language: 'es',
          callback: (newToken) => {
            setToken(newToken);
            setStatus('ready');
          },
          // Tokens die after five minutes. Cloudflare mints a replacement on its
          // own — `refresh-expired` defaults to `auto` — so this only has to
          // hold the button closed until the new one arrives.
          'expired-callback': () => {
            setToken(undefined);
            setStatus('solving');
          },
          // Cloudflare retries on its own every eight seconds by default, so an
          // error is not necessarily final; `reset` is the visitor's way out
          // when it is.
          'error-callback': (_) => {
            setToken(undefined);
            setStatus('error');
          },
          'timeout-callback': () => {
            setToken(undefined);
            setStatus('solving');
          },
        });
      })
      .catch((_) => {
        if (cancelled) return;
        setStatus('error');
      });

    // Whatever token this widget held dies with it, so the state goes back to
    // where it started rather than leaving a stale `ready` behind.
    return () => {
      cancelled = true;
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      widgetIdRef.current = undefined;
      setToken(undefined);
      setStatus('idle');
    };
  }, [enabled, container]);

  // Tokens are single use, so every payment attempt — including a retry after a
  // decline — needs the widget to mint a fresh one.
  const reset = useCallback(() => {
    setToken(undefined);
    setStatus('solving');
    // No widget to reset means its container is currently unmounted; it will
    // mint a fresh token by itself when it comes back.
    if (widgetIdRef.current === undefined) return;

    window.turnstile?.reset(widgetIdRef.current);
  }, []);

  return { containerRef, token, status, reset };
};
